import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { SCHEDULING_CANCEL_PUBLISH_SHIFT } from 'constants/endpoints';
import { METHOD_POST } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { ApiRequestConfigType } from 'requests/index.types';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { toastService } from 'services/toastService';
import { useTranslation } from 'react-i18next';
import { DeleteOpenShiftDialogProps } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/DeleteOpenShiftDialog/DeleteOpenShiftDialog.types';
import { removePositionModalRoutingService } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';

interface DeleteOpenShiftDialogForm {
  handleOpenShift: () => void;
  shiftId: string;
}

const API_DELETE_OpenShift = (shift_id: string): ApiRequestConfigType => ({
  __typename: 'LegacyAPI',
  url: SCHEDULING_CANCEL_PUBLISH_SHIFT(shift_id),
  method: METHOD_POST,
  body: {
    cancel: {
      reason_category: 'not_required',
    },
  },
});

export const DeleteOpenShiftDialogFormContext = createContext({} as DeleteOpenShiftDialogForm);

export const DeleteOpenShiftDialogFormProvider: FC<DeleteOpenShiftDialogProps> = ({
  children,
  shiftId,
}) => {
  const { apiRequest } = useAPIActions();
  const { t } = useTranslation();

  const handleOpenShift = useCallback(
    async () =>
      await apiRequest(API_DELETE_OpenShift(shiftId), {
        onSuccess: () => {
          toastService.success(t('success:SHIFTS_SUCCESSFULLY_DELETED'));
          removePositionModalRoutingService.goToView('OpenShiftsView');
        },
        onError: handleGenericErrorMessage,
      }),
    [apiRequest, shiftId, t],
  );

  const contextValue = useMemo(
    () => ({
      handleOpenShift,
      shiftId,
    }),
    [handleOpenShift, shiftId],
  );

  return (
    <DeleteOpenShiftDialogFormContext.Provider value={contextValue}>
      {children}
    </DeleteOpenShiftDialogFormContext.Provider>
  );
};

export const useDeleteOpenShiftDialogFormProviderFormContext = () =>
  useContext(DeleteOpenShiftDialogFormContext);
